// The binding language. Two entry points:
//   interpolate("WINCH {channels | join(' ')}", ctx)  -> string | string[]
//   test("units.length == 0 && soca.name contains 'WINCH'", ctx) -> boolean
// No eval: a tiny tokenizer and recursive-descent parser over a closed set of filters.

export interface Ctx {
  rec: Record<string, any>;
  globals?: Record<string, any>;               // show, index, copy, copies, ...
  aliases?: Record<string, [string, string][]>;
}

type Tok = { t: 'num' | 'str' | 'id' | 'op' | 'eof'; v: string };

function tokenize(src: string): Tok[] {
  const out: Tok[] = []; let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (/\s/.test(c)) { i++; continue; }
    if (c === '"' || c === "'") {
      let j = i + 1, s = '';
      while (j < src.length && src[j] !== c) { if (src[j] === '\\' && j + 1 < src.length) j++; s += src[j++]; }
      out.push({ t: 'str', v: s }); i = j + 1; continue;
    }
    if (/[0-9]/.test(c) || (c === '-' && /[0-9]/.test(src[i + 1] ?? '') && (out.length === 0 || out[out.length - 1].t === 'op'))) {
      let j = i + 1; while (j < src.length && /[0-9.]/.test(src[j])) j++;
      out.push({ t: 'num', v: src.slice(i, j) }); i = j; continue;
    }
    if (/[A-Za-z_$#]/.test(c)) {
      let j = i + 1; while (j < src.length && /[A-Za-z0-9_$#]/.test(src[j])) j++;
      out.push({ t: 'id', v: src.slice(i, j) }); i = j; continue;
    }
    const two = src.slice(i, i + 2);
    if (['==', '!=', '>=', '<=', '&&', '||'].includes(two)) { out.push({ t: 'op', v: two }); i += 2; continue; }
    if ('|.()[],!<>+'.includes(c)) { out.push({ t: 'op', v: c }); i++; continue; }
    throw new Error(`Unexpected "${c}" in ${src}`);
  }
  out.push({ t: 'eof', v: '' });
  return out;
}

type Node =
  | { k: 'lit'; v: any }
  | { k: 'path'; parts: (string | number)[] }
  | { k: 'pipe'; base: Node; fn: string; args: Node[] }
  | { k: 'bin'; op: string; a: Node; b: Node }
  | { k: 'not'; a: Node };

const WORD_OPS = new Set(['contains', 'startsWith', 'endsWith', 'and', 'or', 'not', 'in']);

class Parser {
  i = 0;
  constructor(private toks: Tok[]) {}
  peek() { return this.toks[this.i]; }
  next() { return this.toks[this.i++]; }
  eat(v: string) { const t = this.peek(); if (t.v === v && t.t !== 'str') { this.i++; return true; } return false; }
  expect(v: string) { if (!this.eat(v)) throw new Error(`Expected ${v}`); }
  parse(): Node { const n = this.or(); if (this.peek().t !== 'eof') throw new Error(`Unexpected ${this.peek().v}`); return n; }
  or(): Node { let a = this.and(); while (this.eat('||') || this.eat('or')) a = { k: 'bin', op: '||', a, b: this.and() }; return a; }
  and(): Node { let a = this.not(); while (this.eat('&&') || this.eat('and')) a = { k: 'bin', op: '&&', a, b: this.not() }; return a; }
  not(): Node { if (this.eat('!') || this.eat('not')) return { k: 'not', a: this.not() }; return this.cmp(); }
  cmp(): Node {
    const a = this.add();
    const t = this.peek();
    if ((t.t === 'op' && ['==', '!=', '>', '<', '>=', '<='].includes(t.v)) || (t.t === 'id' && ['contains', 'startsWith', 'endsWith', 'in'].includes(t.v))) {
      this.next(); return { k: 'bin', op: t.v, a, b: this.add() };
    }
    return a;
  }
  add(): Node { let a = this.pipe(); while (this.eat('+')) a = { k: 'bin', op: '+', a, b: this.pipe() }; return a; }
  pipe(): Node {
    let base = this.primary();
    while (this.eat('|')) {
      const id = this.next(); if (id.t !== 'id') throw new Error('Expected filter name');
      const args: Node[] = [];
      if (this.eat('(')) { if (!this.eat(')')) { do { args.push(this.or()); } while (this.eat(',')); this.expect(')'); } }
      base = { k: 'pipe', base, fn: id.v, args };
    }
    return base;
  }
  primary(): Node {
    const t = this.next();
    if (t.t === 'num') return { k: 'lit', v: parseFloat(t.v) };
    if (t.t === 'str') return { k: 'lit', v: t.v };
    if (t.t === 'op' && t.v === '(') { const n = this.or(); this.expect(')'); return n; }
    if (t.t === 'id') {
      if (t.v === 'true') return { k: 'lit', v: true };
      if (t.v === 'false') return { k: 'lit', v: false };
      if (t.v === 'null') return { k: 'lit', v: null };
      if (WORD_OPS.has(t.v)) throw new Error(`Unexpected ${t.v}`);
      const parts: (string | number)[] = [t.v];
      for (;;) {
        if (this.eat('.')) { const p = this.next(); parts.push(p.t === 'num' ? Number(p.v) : p.v); continue; }
        if (this.peek().v === '[' && this.peek().t === 'op') { this.next(); const p = this.next(); parts.push(p.t === 'num' ? Number(p.v) : p.v); this.expect(']'); continue; }
        break;
      }
      return { k: 'path', parts };
    }
    throw new Error(`Unexpected ${t.v || 'end'}`);
  }
}

const cache = new Map<string, Node>();
export function compile(src: string): Node {
  let n = cache.get(src);
  if (!n) { n = new Parser(tokenize(src)).parse(); cache.set(src, n); }
  return n;
}

export function get(obj: any, parts: (string | number)[]): any {
  let v = obj;
  for (const p of parts) {
    if (v === null || v === undefined) return undefined;
    if (p === 'length' && (Array.isArray(v) || typeof v === 'string')) { v = v.length; continue; }
    if (Array.isArray(v) && typeof p === 'string' && !(p in v)) { v = v.map(x => x?.[p]).filter(x => x !== undefined); continue; }
    v = v[p];
  }
  return v;
}

function str(v: any): string {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.map(str).filter(Boolean).join(', ');
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : String(Math.round(v * 1000) / 1000);
  if (typeof v === 'object') return v.name !== undefined ? str(v.name) : '';
  return String(v);
}

function arr(v: any): any[] { return v === null || v === undefined || v === '' ? [] : Array.isArray(v) ? v : [v]; }

function truthy(v: any): boolean {
  if (Array.isArray(v)) return v.length > 0;
  return !!v && v !== '0';
}

function cmpVal(v: any): any { const n = Number(v); return v !== '' && v !== null && !Array.isArray(v) && !isNaN(n) ? n : str(v).toLowerCase(); }

export function evalNode(n: Node, ctx: Ctx): any {
  switch (n.k) {
    case 'lit': return n.v;
    case 'path': {
      const [h, ...rest] = n.parts;
      if (typeof h === 'string' && h.startsWith('$')) return get(ctx.globals?.[h.slice(1)], rest);
      if (typeof h === 'string' && !(h in ctx.rec) && ctx.globals && h in ctx.globals) return get(ctx.globals[h], rest);
      return get(ctx.rec, n.parts);
    }
    case 'not': return !truthy(evalNode(n.a, ctx));
    case 'bin': {
      if (n.op === '&&') return truthy(evalNode(n.a, ctx)) && truthy(evalNode(n.b, ctx));
      if (n.op === '||') { const a = evalNode(n.a, ctx); return truthy(a) ? a : evalNode(n.b, ctx); }
      const a = evalNode(n.a, ctx), b = evalNode(n.b, ctx);
      switch (n.op) {
        case '+': return typeof a === 'number' && typeof b === 'number' ? a + b : str(a) + str(b);
        case '==': return cmpVal(a) === cmpVal(b);
        case '!=': return cmpVal(a) !== cmpVal(b);
        case '>': return cmpVal(a) > cmpVal(b);
        case '<': return cmpVal(a) < cmpVal(b);
        case '>=': return cmpVal(a) >= cmpVal(b);
        case '<=': return cmpVal(a) <= cmpVal(b);
        case 'contains': return Array.isArray(a) ? a.some(x => str(x).toLowerCase() === str(b).toLowerCase()) : str(a).toLowerCase().includes(str(b).toLowerCase());
        case 'startsWith': return str(a).toLowerCase().startsWith(str(b).toLowerCase());
        case 'endsWith': return str(a).toLowerCase().endsWith(str(b).toLowerCase());
        case 'in': return arr(b).some(x => str(x).toLowerCase() === str(a).toLowerCase());
      }
      return undefined;
    }
    case 'pipe': {
      const v = evalNode(n.base, ctx);
      const args = n.args.map(a => evalNode(a, ctx));
      const f = FILTERS[n.fn];
      if (!f) throw new Error(`Unknown filter "${n.fn}"`);
      return f(v, args, ctx, n.args);
    }
  }
}

type Filter = (v: any, args: any[], ctx: Ctx, raw: Node[]) => any;

/** Shorten by an alias table: first entry whose match is a substring wins. */
export function aliasOf(s: string, table: [string, string][] | undefined, fallbackLen?: number): string {
  const low = s.toLowerCase();
  for (const [m, short] of table ?? []) if (m && low.includes(m.toLowerCase())) return short;
  return fallbackLen ? s.toUpperCase().slice(0, fallbackLen) : s;
}

export const FILTERS: Record<string, Filter> = {
  join: (v, [sep]) => arr(v).map(str).filter(Boolean).join(sep ?? ', '),
  unique: v => [...new Set(arr(v).map(x => (typeof x === 'object' ? x : str(x))))].filter(x => x !== ''),
  upper: v => (Array.isArray(v) ? v.map(x => str(x).toUpperCase()) : str(v).toUpperCase()),
  lower: v => (Array.isArray(v) ? v.map(x => str(x).toLowerCase()) : str(v).toLowerCase()),
  trim: v => str(v).trim(),
  first: v => arr(v)[0],
  last: v => { const a = arr(v); return a[a.length - 1]; },
  count: v => arr(v).length,
  length: v => (typeof v === 'string' ? v.length : arr(v).length),
  map: (v, _a, ctx, raw) => {
    const node = raw[0];
    if (!node) return v;
    const parts = node.k === 'path' ? node.parts : [str(evalNode(node, ctx))];
    return arr(v).map(x => get(x, parts)).filter(x => x !== undefined && x !== null && x !== '');
  },
  where: (v, _a, ctx, raw) => arr(v).filter(x => truthy(evalNode(raw[0], { ...ctx, rec: typeof x === 'object' ? x : { value: x } }))),
  alias: (v, [table, fallbackLen], ctx) => {
    const t = ctx.aliases?.[table ?? 'types'];
    return Array.isArray(v) ? v.map(x => aliasOf(str(x), t, fallbackLen)) : aliasOf(str(v), t, fallbackLen);
  },
  default: (v, [d]) => (str(v) === '' ? d : v),
  ifempty: (v, [d]) => (str(v) === '' ? d : v),
  prefix: (v, [p]) => (str(v) === '' ? '' : str(p) + str(v)),
  suffix: (v, [s]) => (str(v) === '' ? '' : str(v) + str(s)),
  replace: (v, [a, b]) => str(v).split(str(a)).join(str(b ?? '')),
  split: (v, [sep]) => str(v).split(sep ?? ',').map(s => s.trim()).filter(Boolean),
  clean: v => str(v).replace(/\(.*?\)/g, '').replace(/\s+/g, ' ').trim(),
  sort: v => [...arr(v)].sort((a, b) => { const x = cmpVal(a), y = cmpVal(b); return x < y ? -1 : x > y ? 1 : 0; }),
  min: v => { const a = arr(v).map(Number).filter(n => !isNaN(n)); return a.length ? Math.min(...a) : ''; },
  max: v => { const a = arr(v).map(Number).filter(n => !isNaN(n)); return a.length ? Math.max(...a) : ''; },
  sum: v => arr(v).map(Number).filter(n => !isNaN(n)).reduce((s, n) => s + n, 0),
  slice: (v, [a, b]) => (Array.isArray(v) ? v.slice(a, b) : str(v).slice(a, b)),
  each: (v, [fmt]) => arr(v).map(x => str(fmt).split('{}').join(str(x))),
  /** Lines: an array that layout stacks one per line. */
  stack: v => arr(v).map(str),
  /** "A  B / C": pairs per line. */
  pairs: (v, [sep]) => { const a = arr(v).map(str), out: string[] = []; for (let i = 0; i < a.length; i += 2) out.push(a.slice(i, i + 2).join(sep ?? '  ')); return out; },
  /** "3 of 8" with the current copy number. */
  numof: (v, _a, ctx) => `${(ctx.globals?.copy ?? 0) + 1} of ${str(v) || ctx.globals?.copies || 1}`,
  pad: (v, [n, ch]) => str(v).padStart(Number(n) || 0, ch ?? '0'),
};

/** Evaluate a condition string. Empty means true. */
export function test(src: string | undefined, ctx: Ctx): boolean {
  if (!src || !src.trim()) return true;
  return truthy(evalNode(compile(src), ctx));
}

/** Evaluate a single expression (no braces). */
export function evaluate(src: string, ctx: Ctx): any { return evalNode(compile(src), ctx); }

/** Split a template into literal text and {expressions}; braces inside quotes don't count. */
export function scanTemplate(tpl: string): ({ lit: string } | { expr: string })[] {
  const out: ({ lit: string } | { expr: string })[] = [];
  let i = 0, lit = '';
  while (i < tpl.length) {
    if (tpl[i] !== '{') { lit += tpl[i++]; continue; }
    let j = i + 1, q: string | null = null, depth = 1;
    for (; j < tpl.length; j++) {
      const c = tpl[j];
      if (q) { if (c === '\\') j++; else if (c === q) q = null; continue; }
      if (c === '"' || c === "'") q = c;
      else if (c === '{') depth++;
      else if (c === '}' && --depth === 0) break;
    }
    if (j >= tpl.length) { lit += tpl.slice(i); break; }
    if (lit) { out.push({ lit }); lit = ''; }
    out.push({ expr: tpl.slice(i + 1, j) });
    i = j + 1;
  }
  if (lit) out.push({ lit });
  return out;
}

/**
 * Fill a text template. A template that is exactly one {expression} keeps arrays
 * (so layout can stack them); anything mixed with literal text becomes a string.
 */
export function interpolate(tpl: string, ctx: Ctx): string | string[] {
  const parts = scanTemplate(tpl);
  const exprs = parts.filter(p => 'expr' in p);
  if (exprs.length === 1 && parts.every(p => 'expr' in p || !p.lit.trim())) {
    const v = evaluate((exprs[0] as { expr: string }).expr, ctx);
    return Array.isArray(v) ? v.map(str) : str(v);
  }
  return parts.map(p => ('lit' in p ? p.lit : str(evaluate(p.expr, ctx)))).join('');
}

/** Field paths a template mentions, for the "fields used" panel and validation. */
export function fieldsIn(tpl: string): string[] {
  const out = new Set<string>();
  for (const p of scanTemplate(tpl)) {
    if ('expr' in p) { try { walk(compile(p.expr), out); } catch { /* shown by validation */ } }
  }
  return [...out];
}
function walk(n: Node, out: Set<string>) {
  if (n.k === 'path') out.add(n.parts.join('.'));
  else if (n.k === 'pipe') { walk(n.base, out); }
  else if (n.k === 'bin') { walk(n.a, out); walk(n.b, out); }
  else if (n.k === 'not') walk(n.a, out);
}
